<script setup>
/* eslint-disable no-use-before-define */
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import TalkviaFactaOfflineBatchesAPI from 'dashboard/api/talkviaFactaOfflineBatches';

const store = useStore();

const inputClass =
  'w-full reset-base !mb-0 rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none transition-colors placeholder:text-n-slate-10 focus:border-n-brand focus:ring-1 focus:ring-n-brand';
const datePickerIconClass =
  'facta-date-input [color-scheme:dark] [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:rounded-md [&::-webkit-calendar-picker-indicator]:opacity-70 [&::-webkit-calendar-picker-indicator]:invert hover:[&::-webkit-calendar-picker-indicator]:opacity-100';
const dateInputClass = `${inputClass} ${datePickerIconClass}`;
const dateButtonClass =
  'flex h-10 w-full items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-solid-1 px-3 text-left text-sm text-n-slate-12 shadow-sm transition hover:bg-n-alpha-2 focus:border-n-brand focus:outline-none focus:ring-1 focus:ring-n-brand';
const hiddenDateInputClass = 'absolute bottom-0 left-0 size-px opacity-0';

const batches = ref([]);
const selectedBatch = ref(null);
const integration = ref({ configured: false, active: false });
const isLoading = ref(false);
const isLoadingMore = ref(false);
const pagination = ref({ page: 1, hasMore: false, nextPage: null });
const isCreating = ref(false);
const isCreateModalOpen = ref(false);
const showAllCpfs = ref(false);
const logResult = ref(null);
const labelSearch = ref('');
const activeBatchActionsId = ref(null);
const fileInput = ref(null);
const createdFromInputRef = ref(null);
const createdToInputRef = ref(null);
let pollTimer = null;

const audienceModeOptions = [
  { value: 'paste', label: 'Colar', icon: 'i-lucide-clipboard-list' },
  { value: 'labels', label: 'Etiquetas', icon: 'i-lucide-tags' },
  { value: 'file', label: 'Arquivo', icon: 'i-lucide-file-up' },
];

const filters = reactive({
  created_from: '',
  created_to: '',
});

const form = reactive({
  title: '',
  product_type: 'BOTH',
  audience_mode: 'paste',
  cpfs: '',
  label_titles: '',
  file: null,
  scheduled_at: '',
});

const currentResults = computed(() => selectedBatch.value?.results || []);

const accountLabels = computed(() => store.getters['labels/getLabels'] || []);

const selectedLabelTitles = computed(() => splitLabels(form.label_titles));

const filteredLabels = computed(() => {
  const query = labelSearch.value.trim().toLowerCase();
  return accountLabels.value
    .filter(label => !query || label.title.toLowerCase().includes(query))
    .slice(0, 30);
});

const hasSelectedLabel = title => selectedLabelTitles.value.includes(title);

const toggleLabel = title => {
  const next = new Set(selectedLabelTitles.value);
  if (next.has(title)) next.delete(title);
  else next.add(title);
  form.label_titles = Array.from(next).join(',');
};

const toggleBatchActions = batchId => {
  activeBatchActionsId.value =
    activeBatchActionsId.value === batchId ? null : batchId;
};

const closeBatchActions = () => {
  activeBatchActionsId.value = null;
};

const canPauseBatch = batch =>
  ['awaiting', 'processing'].includes(batch?.status);
const canResumeBatch = batch => batch?.status === 'paused';
const canCancelBatch = batch =>
  batch &&
  !['completed', 'partial_success', 'failed', 'timeout', 'cancelled'].includes(
    batch.status
  );

const currentDateTime = computed(() => new Date().toISOString().slice(0, 16));

const splitLabels = value =>
  String(value || '')
    .split(/[,;\n]/)
    .map(item => item.trim())
    .filter(Boolean);

const extractCpfCandidates = value => {
  const content = String(value || '');
  const candidates = [
    ...content.split(/[,;\r\n]+/),
    ...(content.match(/\d{3}\.?\d{3}\.?\d{3}-?\d{2}/g) || []),
  ];

  return Array.from(
    new Set(
      candidates
        .map(candidate => candidate.replace(/\D/g, ''))
        .filter(candidate => candidate.length === 11)
    )
  );
};

const normalizeCpfList = value => extractCpfCandidates(value).join(', ');

const normalizeCpfs = () => {
  form.cpfs = normalizeCpfList(form.cpfs);
};

const normalizeCpfsAfterPaste = () => {
  window.setTimeout(normalizeCpfs, 0);
};

const audienceCount = computed(() => {
  if (form.audience_mode === 'labels')
    return splitLabels(form.label_titles).length;
  if (form.audience_mode === 'file') return form.file ? 1 : 0;
  return extractCpfCandidates(form.cpfs).length;
});

const mergeBatch = batch => {
  const index = batches.value.findIndex(item => item.id === batch.id);
  if (index >= 0)
    batches.value.splice(index, 1, { ...batches.value[index], ...batch });
  else batches.value.unshift(batch);
};

const selectFirstBatch = async () => {
  if (!selectedBatch.value && batches.value.length)
    await selectBatch(batches.value[0]);
};

const selectedProgress = computed(() => {
  const total = Number(selectedBatch.value?.total || 0);
  const processed = Number(selectedBatch.value?.processed || 0);
  return total ? Math.round((processed / total) * 100) : 0;
});

const loadBatches = async ({
  silent = false,
  page = 1,
  append = false,
} = {}) => {
  if (!silent && !append) isLoading.value = true;
  if (append) isLoadingMore.value = true;
  try {
    const { data } = await TalkviaFactaOfflineBatchesAPI.get({
      page,
      per_page: 25,
      created_from: filters.created_from,
      created_to: filters.created_to,
    });
    batches.value = append
      ? [...batches.value, ...(data.batches || [])]
      : data.batches || [];
    pagination.value = {
      page: data.pagination?.page || page,
      hasMore: Boolean(data.pagination?.has_more),
      nextPage: data.pagination?.next_page || null,
    };
    integration.value = data.integration || integration.value;
    if (!silent && !append) await selectFirstBatch();
  } catch {
    if (!silent) useAlert('Não foi possível carregar os lotes Facta.');
  } finally {
    if (!silent && !append) isLoading.value = false;
    if (append) isLoadingMore.value = false;
  }
};

const selectBatch = async (batch, { silent = false } = {}) => {
  try {
    const { data } = await TalkviaFactaOfflineBatchesAPI.show(batch.id);
    selectedBatch.value = data;
    mergeBatch(data);
  } catch {
    if (!silent) useAlert('Não foi possível carregar o lote.');
  }
};

const refreshBatches = async () => {
  await loadBatches({ silent: true, page: 1 });
  if (selectedBatch.value)
    await selectBatch(selectedBatch.value, { silent: true });
};

const loadMoreBatches = async () => {
  if (!pagination.value.hasMore || isLoadingMore.value) return;
  await loadBatches({
    silent: true,
    append: true,
    page: pagination.value.nextPage,
  });
};

const onBatchesScroll = event => {
  const target = event.target;
  const remaining =
    target.scrollHeight - target.scrollTop - target.clientHeight;
  if (remaining < 80) loadMoreBatches();
};

const applyDateFilters = async () => {
  selectedBatch.value = null;
  await loadBatches({ page: 1 });
};

const formatDateFilterLabel = value => {
  if (!value) return 'dd/mm/aaaa';
  const [year, month, day] = value.split('-');
  return `${day}/${month}/${year}`;
};

const openDateInput = inputRef => {
  const input = inputRef.value;
  if (!input) return;
  if (typeof input.showPicker === 'function') {
    input.showPicker();
    return;
  }
  input.click();
};

const createBatch = async () => {
  const pastedCpfs = normalizeCpfList(form.cpfs);
  const hasAudience =
    (form.audience_mode === 'paste' && pastedCpfs) ||
    (form.audience_mode === 'file' && form.file) ||
    (form.audience_mode === 'labels' && form.label_titles.trim());

  if (!hasAudience) {
    useAlert('Cole CPFs, envie um arquivo ou informe etiquetas.');
    return;
  }

  if (form.audience_mode === 'paste') form.cpfs = pastedCpfs;

  const data = new FormData();
  data.append('title', form.title);
  data.append('product_type', form.product_type);
  data.append('audience_mode', form.audience_mode);
  data.append('cpfs', form.audience_mode === 'paste' ? pastedCpfs : '');
  data.append(
    'label_titles',
    form.audience_mode === 'labels' ? form.label_titles : ''
  );
  data.append('scheduled_at', form.scheduled_at);
  if (form.audience_mode === 'file' && form.file)
    data.append('file', form.file);

  isCreating.value = true;
  try {
    const response = await TalkviaFactaOfflineBatchesAPI.create(data);
    mergeBatch(response.data);
    selectedBatch.value = response.data;
    resetForm();
    closeCreateModal();
    useAlert('Lote criado. O worker Facta processará 1 CPF por vez por conta.');
  } catch (error) {
    useAlert(error?.response?.data?.error || 'Não foi possível criar o lote.');
  } finally {
    isCreating.value = false;
  }
};

const onFileChange = event => {
  form.file = event.target.files?.[0] || null;
};

const resetForm = () => {
  form.title = '';
  form.product_type = 'BOTH';
  form.audience_mode = 'paste';
  form.cpfs = '';
  form.label_titles = '';
  form.file = null;
  form.scheduled_at = '';
  if (fileInput.value) fileInput.value.value = '';
};

const openCreateModal = () => {
  isCreateModalOpen.value = true;
};

const closeCreateModal = () => {
  isCreateModalOpen.value = false;
};

const toggleAllCpfs = () => {
  showAllCpfs.value = !showAllCpfs.value;
};

const displayCpf = result =>
  showAllCpfs.value ? result.cpf_full || result.cpf : result.cpf;

const openLog = result => {
  logResult.value = result;
};

const closeLog = () => {
  logResult.value = null;
};

const downloadCsv = async batch => {
  closeBatchActions();
  try {
    const response = await TalkviaFactaOfflineBatchesAPI.download(batch.id);
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `facta-offline-lote-${batch.id}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch {
    useAlert('Não foi possível baixar o CSV.');
  }
};

const deleteBatch = async batch => {
  closeBatchActions();
  try {
    await TalkviaFactaOfflineBatchesAPI.destroy(batch.id);
    batches.value = batches.value.filter(item => item.id !== batch.id);
    if (selectedBatch.value?.id === batch.id)
      selectedBatch.value = batches.value[0] || null;
    if (selectedBatch.value && !selectedBatch.value.results)
      await selectBatch(selectedBatch.value);
    useAlert('Lote excluído.');
  } catch {
    useAlert('Não foi possível excluir o lote.');
  }
};

const updateBatchStatus = async (batch, action) => {
  closeBatchActions();
  try {
    const { data } = await TalkviaFactaOfflineBatchesAPI[action](batch.id);
    selectedBatch.value = data;
    mergeBatch(data);
    useAlert('Lote atualizado.');
  } catch {
    useAlert('Não foi possível atualizar o lote.');
  }
};

const batchHasErrors = batch =>
  Number(batch?.error_count || 0) + Number(batch?.timeout_count || 0) > 0;

const batchVisualStatus = batch => {
  if (batch?.status === 'partial_success' && !batchHasErrors(batch))
    return 'completed';
  return batch?.status;
};

const recentBatchCardClass = batch =>
  selectedBatch.value?.id === batch.id ? 'ring-1 ring-white/25' : '';

const recentBatchCardStyle = batch =>
  ({
    completed: {
      borderColor: 'rgba(16, 185, 129, 0.75)',
      backgroundColor: 'rgba(6, 78, 59, 0.42)',
      color: '#d1fae5',
    },
    processing: {
      borderColor: 'rgba(59, 130, 246, 0.75)',
      backgroundColor: 'rgba(30, 64, 175, 0.38)',
      color: '#dbeafe',
    },
    paused: {
      borderColor: 'rgba(59, 130, 246, 0.75)',
      backgroundColor: 'rgba(30, 64, 175, 0.38)',
      color: '#dbeafe',
    },
    awaiting: {
      borderColor: 'rgba(148, 163, 184, 0.65)',
      backgroundColor: 'rgba(30, 41, 59, 0.42)',
      color: '#e2e8f0',
    },
    partial_success: {
      borderColor: 'rgba(180, 114, 0, 0.95)',
      backgroundColor: 'rgba(74, 44, 0, 0.78)',
      color: '#fde68a',
    },
    failed: {
      borderColor: 'rgba(180, 114, 0, 0.95)',
      backgroundColor: 'rgba(74, 44, 0, 0.78)',
      color: '#fde68a',
    },
    timeout: {
      borderColor: 'rgba(180, 114, 0, 0.95)',
      backgroundColor: 'rgba(74, 44, 0, 0.78)',
      color: '#fde68a',
    },
    cancelled: {
      borderColor: 'rgba(239, 68, 68, 0.75)',
      backgroundColor: 'rgba(127, 29, 29, 0.45)',
      color: '#fee2e2',
    },
  })[batchVisualStatus(batch)] || {
    borderColor: 'rgba(148, 163, 184, 0.65)',
    backgroundColor: 'rgba(30, 41, 59, 0.42)',
    color: '#e2e8f0',
  };

const statusLabel = status =>
  ({
    completed: 'Finalizado',
    processing: 'Em processamento',
    paused: 'Pausado',
    cancelled: 'Cancelado',
    partial_success: 'Concluído com erro',
    failed: 'Concluído com erro',
    timeout: 'Concluído com erro',
    awaiting: 'Não iniciado',
  })[status] || status;

const resultClass = status =>
  ({
    success: 'text-emerald-600',
    authorization_required: 'text-amber-600',
    no_margin: 'text-amber-600',
    error: 'text-red-500',
    timeout: 'text-red-500',
    processing: 'text-blue-600',
    cancelled: 'text-red-500',
  })[status] || 'text-n-slate-11';

const formatDate = value => {
  if (!value) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

const startPolling = () => {
  pollTimer = window.setInterval(refreshBatches, 8000);
};

onMounted(async () => {
  await store.dispatch('labels/get');
  await loadBatches();
  startPolling();
});

onBeforeUnmount(() => {
  if (pollTimer) window.clearInterval(pollTimer);
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/html-closing-bracket-newline, vue/max-attributes-per-line -->
  <main
    class="flex h-full min-h-0 flex-col overflow-auto bg-n-background p-6 text-n-slate-12"
    @click="closeBatchActions"
  >
    <header
      class="mb-6 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between"
    >
      <div class="max-w-3xl">
        <div
          class="mb-2 text-xs font-semibold uppercase tracking-wide text-n-brand"
        >
          Facta offline
        </div>
        <h1 class="m-0 text-3xl font-semibold">Processo em lote Facta</h1>
        <p class="mb-0 mt-2 text-base leading-7 text-n-slate-11">
          Consulte bases offline CLT e FGTS por copiar e colar, arquivo ou
          etiquetas. O CSV pode ser baixado durante o processamento.
        </p>
      </div>
      <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4 text-sm">
        <div
          class="font-semibold"
          :class="
            integration.configured ? 'text-emerald-600' : 'text-amber-600'
          "
        >
          {{
            integration.configured
              ? 'Integração Facta ativa'
              : 'Facta não configurada'
          }}
        </div>
        <div class="mt-1 text-n-slate-11">
          Produtos: {{ integration.products?.join(', ') || 'não informado' }}
        </div>
      </div>
    </header>

    <section class="grid min-h-0 gap-5 xl:grid-cols-[360px_minmax(0,1fr)]">
      <aside class="space-y-5">
        <section class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <div class="space-y-4">
            <div>
              <h2 class="m-0 text-base font-semibold">Novo lote</h2>
              <p class="mb-0 mt-1 text-sm text-n-slate-11">
                Comece colando CPFs ou use etiquetas/arquivo.
              </p>
            </div>
            <button
              type="button"
              class="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-n-brand px-4 text-sm font-semibold text-white shadow-sm hover:bg-n-brand/90"
              @click="openCreateModal"
            >
              <i class="i-lucide-plus size-4" /> Novo lote
            </button>
          </div>
        </section>

        <section class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <h2 class="m-0 text-base font-semibold">Lotes recentes</h2>
          <div class="mt-3 grid gap-2 sm:grid-cols-2">
            <label class="flex flex-col gap-2 text-sm text-n-slate-12">
              <span>Criado de</span>
              <span class="relative block">
                <button
                  type="button"
                  :class="dateButtonClass"
                  @click="openDateInput(createdFromInputRef)"
                >
                  <span class="truncate">
                    {{ formatDateFilterLabel(filters.created_from) }}
                  </span>
                  <span class="i-lucide-calendar-days size-4 shrink-0" />
                </button>
                <input
                  ref="createdFromInputRef"
                  v-model="filters.created_from"
                  type="date"
                  :class="hiddenDateInputClass"
                  tabindex="-1"
                  aria-hidden="true"
                  @input="applyDateFilters"
                />
              </span>
            </label>

            <label class="flex flex-col gap-2 text-sm text-n-slate-12">
              <span>Criado até</span>
              <span class="relative block">
                <button
                  type="button"
                  :class="dateButtonClass"
                  @click="openDateInput(createdToInputRef)"
                >
                  <span class="truncate">
                    {{ formatDateFilterLabel(filters.created_to) }}
                  </span>
                  <span class="i-lucide-calendar-days size-4 shrink-0" />
                </button>
                <input
                  ref="createdToInputRef"
                  v-model="filters.created_to"
                  type="date"
                  :class="hiddenDateInputClass"
                  tabindex="-1"
                  aria-hidden="true"
                  @input="applyDateFilters"
                />
              </span>
            </label>
          </div>
          <div v-if="isLoading" class="mt-3 text-sm text-n-slate-11">
            Carregando...
          </div>
          <div
            v-else
            class="mt-3 max-h-[560px] space-y-2 overflow-auto pr-1"
            @scroll="onBatchesScroll"
          >
            <article
              v-for="batch in batches"
              :key="batch.id"
              class="w-full cursor-pointer rounded-lg border p-4 text-left transition-colors"
              :class="recentBatchCardClass(batch)"
              :style="recentBatchCardStyle(batch)"
              role="button"
              tabindex="0"
              @click="selectBatch(batch)"
              @keydown.enter.prevent="selectBatch(batch)"
              @keydown.space.prevent="selectBatch(batch)"
            >
              <div class="flex items-start justify-between gap-3">
                <span class="min-w-0 truncate text-sm font-semibold">{{
                  batch.title || `Lote #${batch.id}`
                }}</span>
                <div class="relative flex shrink-0 items-center gap-2">
                  <span
                    class="inline-flex rounded-full border border-current/25 bg-black/10 px-2 py-1 text-[11px] font-semibold leading-none"
                    >{{ statusLabel(batchVisualStatus(batch)) }}</span
                  >
                  <button
                    type="button"
                    class="inline-flex size-9 items-center justify-center rounded-lg border border-white/15 bg-black/10 text-current hover:bg-white/10"
                    title="Ações do lote"
                    @click.stop="toggleBatchActions(batch.id)"
                  >
                    <i class="i-lucide-ellipsis-vertical size-4" />
                  </button>
                  <div
                    v-if="activeBatchActionsId === batch.id"
                    class="absolute right-0 top-10 z-20 min-w-44 overflow-hidden rounded-lg border border-n-weak bg-n-solid-1 py-1 text-n-slate-12 shadow-xl"
                    @click.stop
                  >
                    <button
                      type="button"
                      class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-n-slate-11 hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-45"
                      :disabled="!canPauseBatch(batch)"
                      @click="updateBatchStatus(batch, 'pause')"
                    >
                      <i class="i-lucide-pause size-4" /> Pausar
                    </button>
                    <button
                      type="button"
                      class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-n-slate-11 hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-45"
                      :disabled="!canResumeBatch(batch)"
                      @click="updateBatchStatus(batch, 'resume')"
                    >
                      <i class="i-lucide-play size-4" /> Retomar
                    </button>
                    <button
                      type="button"
                      class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-n-ruby-11 hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-45"
                      :disabled="!canCancelBatch(batch)"
                      @click="updateBatchStatus(batch, 'cancel')"
                    >
                      <i class="i-lucide-ban size-4" /> Cancelar
                    </button>
                    <button
                      type="button"
                      class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-n-slate-11 hover:bg-n-alpha-2"
                      @click="downloadCsv(batch)"
                    >
                      <i class="i-lucide-download size-4" /> Baixar CSV
                    </button>
                    <button
                      type="button"
                      class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-n-ruby-11 hover:bg-n-alpha-2"
                      @click="deleteBatch(batch)"
                    >
                      <i class="i-lucide-trash-2 size-4" /> Excluir
                    </button>
                  </div>
                </div>
              </div>
              <div class="mt-4 grid gap-1 text-xs text-current/75">
                <div>
                  <span class="font-medium text-current"
                    >Total/Processado:</span
                  >
                  {{ batch.total }}/{{ batch.processed }}
                </div>
                <div>
                  <span class="font-medium text-current">Data criada:</span>
                  {{ formatDate(batch.created_at) }}
                </div>
                <div>
                  <span class="font-medium text-current">Data finalizado:</span>
                  {{ formatDate(batch.finished_at) }}
                </div>
              </div>
            </article>
            <div
              v-if="isLoadingMore"
              class="py-2 text-center text-xs text-n-slate-11"
            >
              Carregando mais...
            </div>
          </div>
        </section>
      </aside>

      <section class="min-w-0 rounded-xl border border-n-weak bg-n-solid-1 p-5">
        <div v-if="!selectedBatch" class="text-sm text-n-slate-11">
          Selecione ou crie um lote para acompanhar os resultados.
        </div>
        <template v-else>
          <div
            class="flex flex-col gap-3 md:flex-row md:items-start md:justify-between"
          >
            <div>
              <h2 class="m-0 text-xl font-semibold">
                {{ selectedBatch.title || `Lote #${selectedBatch.id}` }}
              </h2>
              <p class="mb-0 mt-1 text-sm text-n-slate-11">
                {{ selectedBatch.product_type }} · criado em
                {{ formatDate(selectedBatch.created_at) }}
              </p>
            </div>
            <div class="flex flex-wrap gap-2">
              <button
                v-if="canPauseBatch(selectedBatch)"
                type="button"
                class="inline-flex items-center justify-center gap-2 rounded-lg border border-n-weak px-4 py-2 text-sm font-medium"
                @click="updateBatchStatus(selectedBatch, 'pause')"
              >
                <i class="i-lucide-pause size-4" /> Pausar
              </button>
              <button
                v-if="canResumeBatch(selectedBatch)"
                type="button"
                class="inline-flex items-center justify-center gap-2 rounded-lg border border-n-weak px-4 py-2 text-sm font-medium"
                @click="updateBatchStatus(selectedBatch, 'resume')"
              >
                <i class="i-lucide-play size-4" /> Retomar
              </button>
              <button
                v-if="canCancelBatch(selectedBatch)"
                type="button"
                class="inline-flex items-center justify-center gap-2 rounded-lg border border-n-ruby-7 px-4 py-2 text-sm font-medium text-n-ruby-11"
                @click="updateBatchStatus(selectedBatch, 'cancel')"
              >
                <i class="i-lucide-ban size-4" /> Cancelar
              </button>
              <button
                type="button"
                class="inline-flex items-center justify-center gap-2 rounded-lg border border-n-weak px-4 py-2 text-sm font-medium"
                @click="downloadCsv(selectedBatch)"
              >
                <i class="i-lucide-download size-4" /> Baixar CSV
              </button>
            </div>
          </div>

          <div class="mt-5 grid gap-3 md:grid-cols-5">
            <div class="rounded-lg bg-n-solid-2 p-3">
              <div class="text-2xl font-semibold">
                {{ selectedBatch.total }}
              </div>
              <div class="text-xs text-n-slate-10">Total</div>
            </div>
            <div class="rounded-lg bg-n-solid-2 p-3">
              <div class="text-2xl font-semibold">
                {{ selectedBatch.processed }}
              </div>
              <div class="text-xs text-n-slate-10">Processados</div>
            </div>
            <div class="rounded-lg bg-n-solid-2 p-3">
              <div class="text-2xl font-semibold text-emerald-600">
                {{ selectedBatch.success_count }}
              </div>
              <div class="text-xs text-n-slate-10">Sucesso</div>
            </div>
            <div class="rounded-lg bg-n-solid-2 p-3">
              <div class="text-2xl font-semibold text-amber-600">
                {{ selectedBatch.authorization_required_count }}
              </div>
              <div class="text-xs text-n-slate-10">Autorização</div>
            </div>
            <div class="rounded-lg bg-n-solid-2 p-3">
              <div class="text-2xl font-semibold text-red-500">
                {{ selectedBatch.error_count + selectedBatch.timeout_count }}
              </div>
              <div class="text-xs text-n-slate-10">Erros</div>
            </div>
          </div>

          <div class="mt-5 h-2 overflow-hidden rounded-full bg-n-alpha-2">
            <div
              class="h-full bg-n-brand transition-all"
              :style="{ width: `${selectedProgress}%` }"
            />
          </div>

          <div class="mt-5 overflow-hidden rounded-xl border border-n-weak">
            <table class="w-full text-left text-sm">
              <thead class="bg-n-solid-2 text-xs uppercase text-n-slate-10">
                <tr>
                  <th class="px-3 py-2">
                    <button
                      type="button"
                      class="inline-flex items-center gap-2 rounded-lg text-left font-semibold hover:text-n-brand"
                      :title="
                        showAllCpfs
                          ? 'Ocultar CPFs completos'
                          : 'Ver CPFs completos'
                      "
                      @click="toggleAllCpfs"
                    >
                      CPF
                      <i
                        class="size-4"
                        :class="
                          showAllCpfs ? 'i-lucide-eye-off' : 'i-lucide-eye'
                        "
                      />
                    </button>
                  </th>
                  <th class="px-3 py-2">Produto</th>
                  <th class="px-3 py-2">Margem</th>
                  <th class="px-3 py-2">Status</th>
                  <th class="px-3 py-2">Resumo</th>
                  <th class="px-3 py-2">Fim</th>
                  <th class="px-3 py-2 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="result in currentResults"
                  :key="result.id"
                  class="border-t border-n-weak"
                >
                  <td class="px-3 py-2 font-medium">
                    {{ displayCpf(result) }}
                  </td>
                  <td class="px-3 py-2">{{ result.product_type }}</td>
                  <td class="px-3 py-2 font-medium text-emerald-600">
                    {{ result.margin || '-' }}
                  </td>
                  <td
                    class="px-3 py-2 font-semibold"
                    :class="resultClass(result.status)"
                  >
                    {{ result.status }}
                  </td>
                  <td class="px-3 py-2 text-n-slate-11">
                    {{ result.response_summary || result.error_message || '-' }}
                  </td>
                  <td class="px-3 py-2 text-n-slate-10">
                    {{ formatDate(result.finished_at) }}
                  </td>
                  <td class="px-3 py-2">
                    <div class="flex justify-end gap-1">
                      <button
                        type="button"
                        class="rounded-lg p-2 text-n-slate-11 hover:bg-n-alpha-2"
                        title="Ver log do retorno"
                        @click="openLog(result)"
                      >
                        <i class="i-lucide-file-search size-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </section>
    </section>

    <div
      v-if="isCreateModalOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      @click.self="closeCreateModal"
    >
      <form
        class="max-h-[85vh] w-full max-w-3xl overflow-auto rounded-xl border border-n-weak bg-n-solid-1 p-6 shadow-2xl"
        @submit.prevent="createBatch"
      >
        <div class="mb-4 flex items-center justify-between gap-3">
          <h2 class="m-0 text-lg font-semibold">Novo lote</h2>
          <button
            type="button"
            class="rounded-lg p-2 text-n-slate-11 hover:bg-n-alpha-2"
            @click="closeCreateModal"
          >
            <i class="i-lucide-x size-5" />
          </button>
        </div>
        <div class="mt-4 space-y-4">
          <label class="space-y-1 text-sm font-medium">
            <span>Nome do lote</span>
            <input
              v-model="form.title"
              :class="inputClass"
              placeholder="Facta FGTS junho"
            />
          </label>
          <label class="space-y-1 text-sm font-medium">
            <span>Produto</span>
            <select v-model="form.product_type" :class="inputClass">
              <option value="BOTH">CLT + FGTS</option>
              <option value="CLT">Somente CLT</option>
              <option value="FGTS">Somente FGTS</option>
            </select>
          </label>

          <div class="flex flex-col gap-2">
            <label class="mb-0.5 text-sm font-medium text-n-slate-12">
              Público
            </label>
            <div class="grid grid-cols-3 gap-2">
              <button
                v-for="option in audienceModeOptions"
                :key="option.value"
                type="button"
                class="flex h-10 items-center justify-center gap-2 rounded-lg border px-2 text-sm font-medium"
                :class="
                  form.audience_mode === option.value
                    ? 'border-n-brand bg-n-alpha-2 text-n-slate-12'
                    : 'border-n-weak bg-n-alpha-black2 text-n-slate-11 hover:bg-n-alpha-2'
                "
                @click="form.audience_mode = option.value"
              >
                <span :class="option.icon" class="size-4" />
                <span class="truncate">{{ option.label }}</span>
              </button>
            </div>

            <div v-if="form.audience_mode === 'labels'" class="space-y-3">
              <input
                v-model="labelSearch"
                :class="inputClass"
                placeholder="Buscar etiquetas cadastradas"
              />
              <div
                v-if="selectedLabelTitles.length"
                class="flex flex-wrap gap-2"
              >
                <button
                  v-for="title in selectedLabelTitles"
                  :key="`selected-${title}`"
                  type="button"
                  class="inline-flex items-center gap-2 rounded-full border border-n-brand bg-n-brand/10 px-3 py-1 text-xs font-medium text-n-brand"
                  @click="toggleLabel(title)"
                >
                  {{ title }}
                  <i class="i-lucide-x size-3" />
                </button>
              </div>
              <div
                class="max-h-44 overflow-auto rounded-lg border border-n-weak bg-n-solid-2 p-2"
              >
                <button
                  v-for="label in filteredLabels"
                  :key="label.id || label.title"
                  type="button"
                  class="mb-2 mr-2 inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm"
                  :class="
                    hasSelectedLabel(label.title)
                      ? 'border-n-brand bg-n-brand/10 text-n-brand'
                      : 'border-n-weak bg-n-solid-1 text-n-slate-11 hover:border-n-brand'
                  "
                  @click="toggleLabel(label.title)"
                >
                  <span
                    class="size-2 rounded-full"
                    :style="{ backgroundColor: label.color || '#64748b' }"
                  />
                  {{ label.title }}
                </button>
                <div
                  v-if="!filteredLabels.length"
                  class="p-2 text-sm text-n-slate-11"
                >
                  Nenhuma etiqueta encontrada.
                </div>
              </div>
              <p class="mb-0 text-xs text-n-slate-11">
                Somente contatos com CPF cadastrado serão enviados para o lote.
              </p>
            </div>

            <textarea
              v-else-if="form.audience_mode === 'paste'"
              v-model="form.cpfs"
              :class="`${inputClass} min-h-36`"
              placeholder="Cole CPFs. Separe por linha, vírgula ou ponto e vírgula."
              @blur="normalizeCpfs"
              @paste="normalizeCpfsAfterPaste"
            />

            <label
              v-else
              class="flex min-h-28 cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-n-weak bg-n-alpha-black2 px-4 py-5 text-center hover:bg-n-alpha-2"
            >
              <span class="i-lucide-file-up size-5 text-n-slate-11" />
              <span class="text-sm font-medium text-n-slate-12">
                Enviar CSV ou TXT
              </span>
              <span class="text-xs text-n-slate-11">
                {{
                  form.file?.name ||
                  'O arquivo pode ter CPFs em qualquer coluna.'
                }}
              </span>
              <input
                ref="fileInput"
                class="hidden"
                type="file"
                accept=".csv,.txt,text/csv,text/plain"
                @change="onFileChange"
              />
            </label>
            <p class="mb-0 text-xs text-n-slate-11">
              {{ audienceCount }} destinatário(s) encontrado(s)
            </p>
          </div>

          <label class="space-y-1 text-sm font-medium">
            <span>Horário agendado</span>
            <input
              v-model="form.scheduled_at"
              :class="dateInputClass"
              type="datetime-local"
              :min="currentDateTime"
              placeholder="dd/mm/aaaa, --:--"
            />
          </label>

          <button
            type="submit"
            class="w-full rounded-lg bg-n-brand px-4 py-2 text-sm font-medium text-white"
            :disabled="isCreating || !integration.configured"
          >
            {{ isCreating ? 'Criando...' : 'Criar lote' }}
          </button>
        </div>
      </form>
    </div>

    <div
      v-if="logResult"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      @click.self="closeLog"
    >
      <section
        class="max-h-[85vh] w-full max-w-4xl overflow-auto rounded-xl border border-n-weak bg-n-solid-1 p-5 shadow-2xl"
      >
        <div class="mb-4 flex items-center justify-between gap-3">
          <div>
            <h2 class="m-0 text-lg font-semibold">Log do retorno</h2>
            <p class="mb-0 mt-1 text-sm text-n-slate-11">
              {{ logResult.product_type }} ·
              {{ logResult.cpf_full || logResult.cpf }}
            </p>
          </div>
          <button
            type="button"
            class="rounded-lg p-2 text-n-slate-11 hover:bg-n-alpha-2"
            @click="closeLog"
          >
            <i class="i-lucide-x size-5" />
          </button>
        </div>
        <pre
          class="max-h-[60vh] overflow-auto rounded-lg bg-n-solid-2 p-4 text-xs leading-5 text-n-slate-11"
          >{{ JSON.stringify(logResult.raw_response || {}, null, 2) }}</pre
        >
      </section>
    </div>
  </main>
</template>

<style scoped>
.facta-date-input::-webkit-calendar-picker-indicator {
  filter: invert(1);
  opacity: 0.7;
}

.facta-date-input::-webkit-calendar-picker-indicator:hover {
  opacity: 1;
}
</style>
